import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import wirelessApi from '../../services/wirelessApi';

/**
 * Create or edit a wireless engagement.
 *
 * Separate from CreateTestModal rather than a variant of it. That form is
 * twelve hundred lines of offensive scope — network ranges, application URLs,
 * autonomous orchestration depth, source languages — and a wireless assessment
 * needs none of it. Gating all of that behind conditionals would leave one form
 * that is hard to read and easy to break for both kinds of engagement.
 *
 * What a wireless engagement actually needs is a customer, a window, the place
 * that was surveyed, the radio that surveyed it, and what the rules of
 * engagement permit. The scope that matters — which access points may be tested
 * — is not set here at all: it is built by authorising targets as the survey
 * finds them, which is the only point at which anyone can see what is in range.
 */

const WIRELESS_SUBTYPE_ID = 20;

const STATUSES = ['not started', 'in progress', 'finished'];

const ROE_FLAGS = [
  ['deauth_authorized', 'Deauthentication', 'Disconnects clients. A denial of service.'],
  ['rogue_ap_authorized', 'Rogue access points', 'Impersonating a corporate SSID.'],
  ['wps_authorized', 'WPS attacks', 'PIN recovery. Can lock the access point.'],
  ['client_attack_authorized', 'Client-side attacks', 'Directed at staff devices.'],
];

function CreateWirelessEngagementModal({
  open,
  onClose,
  onCreated,
  onNotify,
  customers = [],
  engagement: editEngagement,
}) {
  const isEditMode = Boolean(editEngagement);

  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [start, setStart] = useState('');
  const [finish, setFinish] = useState('');
  const [comments, setComments] = useState('');
  const [status, setStatus] = useState('not started');
  const [location, setLocation] = useState('');
  const [adapter, setAdapter] = useState('');
  const [roeNotes, setRoeNotes] = useState('');
  const [flags, setFlags] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const notify = (message, type = 'error') =>
    onNotify ? onNotify(message, type) : console.warn(message);

  useEffect(() => {
    if (!open) return;
    if (!isEditMode) {
      setTitle(''); setCustomerId(''); setStart(''); setFinish('');
      setComments(''); setStatus('not started'); setLocation('');
      setAdapter(''); setRoeNotes(''); setFlags({});
      return;
    }
    setTitle(editEngagement.title || '');
    setCustomerId(editEngagement.customer_id || editEngagement.customer?.id || '');
    setStart((editEngagement.start || '').slice(0, 10));
    setFinish((editEngagement.finish || '').slice(0, 10));
    setComments(editEngagement.comments || '');
    setStatus(editEngagement.status || 'not started');
    // The wireless fields live on the scope, which the list payload carries.
    (async () => {
      try {
        const roe = await wirelessApi.getRoE(editEngagement.id);
        setLocation(roe.wireless_location || '');
        setAdapter(roe.wireless_adapter || '');
        setRoeNotes(roe.wireless_roe_notes || '');
        setFlags(ROE_FLAGS.reduce((acc, [key]) => ({ ...acc, [key]: Boolean(roe[key]) }), {}));
      } catch (err) {
        console.error('Could not load the wireless rules of engagement', err);
      }
    })();
  }, [open, isEditMode, editEngagement]);

  if (!open) return null;

  const saveWirelessScope = async (engagementId) => {
    // Goes through the wireless RoE endpoint rather than the offensive scope
    // one: these are the only scope fields a wireless engagement has, and that
    // endpoint already validates and audits changes to them.
    await wirelessApi.updateRoE(engagementId, {
      wireless_location: location || null,
      wireless_adapter: adapter || null,
      wireless_roe_notes: roeNotes || null,
      ...ROE_FLAGS.reduce((acc, [key]) => ({ ...acc, [key]: Boolean(flags[key]) }), {}),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return notify('A title is required');
    if (!customerId) return notify('Select a customer');
    if (!start || !finish) return notify('Start and finish dates are required');
    if (new Date(finish) < new Date(start)) return notify('Finish date must be after start date');

    setSubmitting(true);
    const token = localStorage.getItem('token');
    const opts = {
      headers: { access_token: `Bearer ${token}`, 'Content-Type': 'application/json' },
      withCredentials: true,
    };

    try {
      if (isEditMode) {
        await axios.put(`${Config.APIBaseURL}/engagement/update/${editEngagement.id}`, {
          title, start, finish, comments, status,
        }, opts);
        await saveWirelessScope(editEngagement.id);
        notify('Wireless engagement updated', 'success');
      } else {
        const formData = new FormData();
        formData.append('engagementData', JSON.stringify({
          subtype_id: WIRELESS_SUBTYPE_ID,
          framework_id: 1,
          title,
          customer_id: Number(customerId),
          start,
          finish,
          comments,
          status,
          permission_level: 0,
          name: title,
          description: comments,
          timeline: { start, end: finish, allowed_windows: {} },
        }));
        // Empty, deliberately. A wireless engagement's scope is the authorised
        // access point list, which cannot be written before a survey has found
        // anything — the backend requires the field, so it gets an empty one.
        formData.append('scopeData', JSON.stringify({
          targets: [], external_targets: [], excluded_targets: [],
          objectives: [], constraints: [], credentials_provided: false,
        }));

        const response = await axios.post(
          `${Config.APIBaseURL}/engagement/offensive/add/`, formData,
          { headers: { 'Content-Type': 'multipart/form-data', access_token: `Bearer ${token}` },
            withCredentials: true },
        );
        const created = response.data?.id || response.data?.engagement?.id;
        if (created) {
          try {
            await saveWirelessScope(created);
          } catch (err) {
            // The engagement exists; only the wireless fields failed. Say so
            // rather than implying nothing was created.
            console.error('Engagement created but its wireless scope was not saved', err);
            notify('Engagement created, but the location and rules of engagement were not saved. Edit it to set them.', 'error');
          }
        }
        notify('Wireless engagement created', 'success');
      }
      onCreated && onCreated();
      onClose && onClose();
    } catch (error) {
      console.error('Error saving wireless engagement:', error);
      notify(error.response?.data?.detail || error.message || 'Could not save the engagement');
    } finally {
      setSubmitting(false);
    }
  };

  const field = 'w-full rounded-md bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 focus:border-indigo-500 focus:outline-none';
  const label = 'block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white dark:bg-slate-800 shadow-xl">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {isEditMode ? 'Edit wireless engagement' : 'New wireless engagement'}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            The networks to be tested are not set here — they are authorised from the
            survey, once you can see what is actually in range.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className={label}>Title</label>
            <input className={field} value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="e.g. Wireless assessment — main branch" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={label}>Customer</label>
              <select className={field} value={customerId} disabled={isEditMode}
                      onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">Select…</option>
                {/* The page maps customers to {id, label} before passing them
                    down — CreateTestModal reads c.label. Reading only
                    short_name/name rendered an option per customer with no
                    text in it, so the list looked empty rather than broken. */}
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label || c.short_name || c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Status</label>
              <select className={field} value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Start</label>
              <input type="date" className={field} value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <label className={label}>Finish</label>
              <input type="date" className={field} value={finish} onChange={(e) => setFinish(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={label}>Location to survey</label>
              <input className={field} value={location} onChange={(e) => setLocation(e.target.value)}
                     placeholder="e.g. Main branch, ground floor" />
              <p className="text-[10px] text-slate-500 mt-1">
                The scope boundary of a wireless assessment is a place, and this appears
                as such in the report.
              </p>
            </div>
            <div>
              <label className={label}>Capture hardware</label>
              <input className={field} value={adapter} onChange={(e) => setAdapter(e.target.value)}
                     placeholder="e.g. Alfa AWUS036ACM (MT7612U)" />
            </div>
          </div>

          <div>
            <label className={label}>Notes</label>
            <textarea rows={2} className={field} value={comments}
                      onChange={(e) => setComments(e.target.value)} />
          </div>

          <div className="rounded-lg border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 p-3">
            <div className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">
              Disruptive techniques authorised by the signed rules of engagement
            </div>
            <div className="space-y-2">
              {ROE_FLAGS.map(([key, name, note]) => (
                <label key={key} className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={Boolean(flags[key])}
                         onChange={(e) => setFlags((p) => ({ ...p, [key]: e.target.checked }))}
                         className="mt-0.5 w-3.5 h-3.5 rounded border-slate-400 text-indigo-600 focus:ring-0" />
                  <span className="text-xs text-slate-700 dark:text-slate-300">
                    {name} <span className="text-slate-500">— {note}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              Leave unticked unless the signed document permits it. Tests in these classes
              are refused server-side and recorded as not authorised, which is what the
              report should say.
            </p>
            <textarea rows={2} className={`${field} mt-2`} value={roeNotes}
                      onChange={(e) => setRoeNotes(e.target.value)}
                      placeholder="Clause or caveat worth recording (optional)" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-md border border-slate-300 dark:border-slate-600 text-sm text-slate-600 dark:text-slate-300">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
                    className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {submitting ? 'Saving…' : isEditMode ? 'Save changes' : 'Create engagement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateWirelessEngagementModal;
